import { useEffect, useMemo, useRef } from 'react'

import { useAddressStore } from '@features/shipping-methods'

import { useSlotStore } from '@widgets/slots/model/useSlotStore'

import { Slot, useAvailableSlotsQuery, useZonesForGeoPointLazyQuery } from '@shared/generated'

type UseSyncSlotProps = {
  pendingOrderSlotId?: string | null;
  isAvailable?: boolean;
}

/**
 * Хук для синхронизации слота между локальным стором и бэкендом
 * 
 * 1. Загружает зоны для выбранного адреса (если нет zoneId)
 * 2. Синхронизирует selectedSlot из pendingOrder.slot_id при загрузке
 * 3. Автоматически выбирает первый доступный слот, если ничего не выбрано
 */
export function useSyncSlot ({ pendingOrderSlotId, isAvailable = true }: UseSyncSlotProps) {
  const { selectedSlot, setSelectedSlot } = useSlotStore()
  const { shopId, zoneId, deliveryType, selectedAddress, setZoneId, setDeliveryType } = useAddressStore()
  const initialSyncDone = useRef(false)
  const zonesFetched = useRef(false)

  const [ fetchZonesForGeopoint ] = useZonesForGeoPointLazyQuery()

  // Загрузка зон при старте, если есть адрес но нет zoneId
  useEffect(() => {
    if (!isAvailable || zonesFetched.current) return
    if (deliveryType === 'pickup') return
    if (zoneId) {
      zonesFetched.current = true
      return
    }

    const geoPoint = selectedAddress?.customerGeoPoint
    if (!geoPoint?.latitude || !geoPoint?.longitude) return

    const loadZones = async () => {
      try {
        const { data } = await fetchZonesForGeopoint({
          variables: {
            point: {
              latitude: geoPoint.latitude,
              longitude: geoPoint.longitude,
            },
          },
        })

        const fetchedZoneId = data?.zonesForGeoPoint?.[0]?.firstAvailableSlot?.zone_id
        if (fetchedZoneId) {
          setZoneId(fetchedZoneId)
          setDeliveryType('slot')
          zonesFetched.current = true
        }
      }
      catch (error) {
        console.error('Error fetching zones for address:', error)
      }
    }

    loadZones()
  }, [ isAvailable, selectedAddress, zoneId, deliveryType, fetchZonesForGeopoint, setZoneId, setDeliveryType ])

  const variables = useMemo(() => {
    // Для слотовой доставки используем ТОЛЬКО zoneId
    if (deliveryType === 'slot') return { zoneId }
    return { shopId }
  }, [ deliveryType, zoneId, shopId ])

  // Для slot delivery нужен zoneId, для остальных - shopId
  const shouldFetch = isAvailable && (deliveryType === 'slot' ? !!zoneId : !!shopId)

  const { data: slotsData, loading } = useAvailableSlotsQuery({
    variables,
    skip: !shouldFetch,
  })

  const slots = slotsData?.availableSlots as Slot[] | undefined

  // Синхронизация из pendingOrder.slot_id при загрузке
  useEffect(() => {
    if (loading || !shouldFetch) return
    if (!slots || slots.length === 0) return
    if (initialSyncDone.current) return

    // Если есть slot_id из бэкенда - находим и устанавливаем этот слот
    if (pendingOrderSlotId) {
      const matchingSlot = slots.find(slot => slot?.id === pendingOrderSlotId)
      if (matchingSlot) {
        setSelectedSlot(matchingSlot)
        initialSyncDone.current = true
        return
      }
    }

    // Если текущий selectedSlot валиден (есть в списке доступных) - оставляем
    if (selectedSlot) {
      const isCurrentSlotValid = slots.some(slot => slot?.id === selectedSlot.id)
      if (isCurrentSlotValid) {
        initialSyncDone.current = true
        return
      }
    }

    // Автовыбор первого слота, если ничего не выбрано или выбранный невалиден
    const firstSlot = slots[0]
    if (firstSlot) {
      setSelectedSlot(firstSlot)
      initialSyncDone.current = true
    }
  }, [ slots, pendingOrderSlotId, selectedSlot, setSelectedSlot, loading, shouldFetch ])

  // Сброс флага при смене магазина/зоны
  useEffect(() => {
    initialSyncDone.current = false
  }, [ shopId, zoneId ])

  return {
    slots,
    loading,
    selectedSlot,
    hasSlot: !!selectedSlot?.id,
  }
}
